import { useState, useRef, useCallback, useEffect } from 'react';

export function useDropdownPortal(isOpen = false) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, bottom: 0, left: 0, width: 0, dropUp: false });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      // Flip the menu above the trigger when there isn't enough room below it
      // (e.g. the field sits near the bottom of a modal/viewport).
      const estimatedMenuHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropUp = spaceBelow < estimatedMenuHeight && rect.top > spaceBelow;
      setDropdownPos({
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
        dropUp,
      });
    }
  }, []);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, updatePosition]);

  const isOutside = useCallback((event: MouseEvent) => {
    return (
      (!triggerRef.current || !triggerRef.current.contains(event.target as Node)) &&
      (!menuRef.current || !menuRef.current.contains(event.target as Node))
    );
  }, []);

  return { triggerRef, menuRef, dropdownPos, updatePosition, isOutside };
}
